import { useState, useMemo, useRef, useEffect, useLayoutEffect, useCallback, useId } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { FiSend, FiPlus } from "react-icons/fi";
import { HiOutlineUsers } from "react-icons/hi";
import { MdAccountBalanceWallet, MdPersonAddAlt1 } from "react-icons/md";
import { useAuth } from "../../../context/AuthProvider";
import { useIsAdmin } from "../hooks/useIsAdmin";
import "./QuickActionsLauncher.css";

const emitTelemetry = (name, detail) => {
  try {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new CustomEvent(name, { detail }));
    }
  } catch {
    // no-op
  }
};

export default function QuickActionsLauncher({
  buttonClassName = "",
  menuPlacement = "auto",
  showLabel = true,
  variant = "header",
  visible = true,
  enableHotkey = true,
}) {
  const navigate = useNavigate();
  const { hasRole, user } = useAuth();
  const isBusinessAdmin = useIsAdmin();

  const btnRef = useRef(null);
  const menuRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0, arrowLeft: 24 });
  const [activeIndex, setActiveIndex] = useState(0);
  const menuId = useId();
  const buttonId = `${menuId}-btn`;
  const itemRefs = useRef([]);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setActiveIndex(0);
    btnRef.current?.focus();
  }, []);

  const focusItem = useCallback(
    (index) => {
      const el = itemRefs.current[index];
      if (el) {
        el.focus();
        setActiveIndex(index);
      }
    },
    []
  );

  const actions = useMemo(() => {
    const list = [
      {
        key: "new-transaction",
        label: "New transaction",
        description: "Record a new money transfer",
        Icon: FiSend,
        onSelect: () => navigate("/home/transfer", { state: { quickAction: "new-transaction" } }),
      },
      {
        key: "new-customer",
        label: "New customer",
        description: "Add a sender or recipient",
        Icon: HiOutlineUsers,
        onSelect: () => navigate("/home/customers", { state: { quickAction: "new-customer" } }),
      },
    ];

    if (isBusinessAdmin) {
      list.push(
        {
          key: "new-balance",
          label: "New balance entry",
          description: "Fund or settle agent float",
          Icon: MdAccountBalanceWallet,
          onSelect: () => navigate("/home/add-balance", { state: { quickAction: "new-balance" } }),
        },
        {
          key: "invite-user",
          label: "Add new user",
          description: "Invite teammates to Baknay",
          Icon: MdPersonAddAlt1,
          onSelect: () =>
            navigate("/home/create-user", {
              state: { showBackButton: true, quickAction: "new-user" },
            }),
        }
      );
    } else if (hasRole?.("AGENT")) {
      list.push({
        key: "agent-balance",
        label: "My balance",
        description: "Review my payouts & float",
        Icon: MdAccountBalanceWallet,
        onSelect: () => navigate(`/home/agent/${user?.uuid ?? ""}`),
      });
    }

    return list;
  }, [hasRole, isBusinessAdmin, navigate, user?.uuid]);

  useEffect(() => {
    itemRefs.current = [];
  }, [actions]);

  const updatePosition = useCallback(
    ({ force = false } = {}) => {
      if ((!open && !force) || typeof window === "undefined") return;
      const trigger = btnRef.current;
      if (!trigger) return;
      const rect = trigger.getBoundingClientRect();
      const width = menuRef.current?.offsetWidth ?? 320;
      const viewportWidth = window.innerWidth;
      const gap = 8;

      let left = rect.left + rect.width - width;
      if (menuPlacement === "start") {
        left = rect.left;
      }
      left = Math.max(8, Math.min(left, viewportWidth - width - 8));
      let top = rect.bottom + gap;
      if (rect.width === 0 && rect.height === 0) {
        left = Math.max(8, viewportWidth - width - 8);
        top = gap * 2 + 48;
      }
      const arrowLeft = Math.min(width - 24, Math.max(24, rect.left + rect.width / 2 - left));
      setPosition({ top, left, arrowLeft });
    },
    [menuPlacement, open]
  );

  useLayoutEffect(() => {
    if (!open || typeof window === "undefined") return undefined;
    const id = requestAnimationFrame(updatePosition);
    const handleResize = () => updatePosition();
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", handleResize, true);
    return () => {
      cancelAnimationFrame(id);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", handleResize, true);
    };
  }, [open, updatePosition]);

  useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;
    const handlePointerDown = (event) => {
      if (menuRef.current?.contains(event.target) || btnRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.stopPropagation();
        closeMenu();
      }
    };
    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeMenu, open]);

  useEffect(() => {
    if (!visible || !enableHotkey || typeof window === "undefined") return undefined;
    const handleHotkey = (event) => {
      const isModifier = event.metaKey || event.ctrlKey;
      if (!isModifier || event.key.toLowerCase() !== "k") return;
      event.preventDefault();
      updatePosition({ force: true });
      setOpen(true);
    };
    window.addEventListener("keydown", handleHotkey);
    return () => window.removeEventListener("keydown", handleHotkey);
  }, [enableHotkey, focusItem, updatePosition, variant, visible]);

  useEffect(() => {
    if (!open) return;
    emitTelemetry("quick_actions.open", { source: variant });
    setTimeout(() => focusItem(0), 0);
  }, [focusItem, open, variant]);

  const selectAction = useCallback(
    (action) => {
      emitTelemetry("quick_actions.select", { action_type: action.key });
      setOpen(false);
      setTimeout(() => action.onSelect(), 0);
    },
    []
  );

  const handleButtonClick = () => {
    if (!actions.length) return;
    if (!open) updatePosition({ force: true });
    setOpen((prev) => !prev);
  };

  const handleMenuKeyDown = (event) => {
    if (!actions.length) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      const next = (activeIndex + 1) % actions.length;
      focusItem(next);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      const prev = (activeIndex - 1 + actions.length) % actions.length;
      focusItem(prev);
    } else if (event.key === "Tab") {
      event.preventDefault();
      const delta = event.shiftKey ? -1 : 1;
      const next = (activeIndex + delta + actions.length) % actions.length;
      focusItem(next);
    }
  };

  if (!visible) return null;

  return (
    <>
      <button
        ref={btnRef}
        id={buttonId}
        type="button"
        className={`quick-actions-btn btn btn-sm btn-primary d-inline-flex align-items-center gap-1 ${buttonClassName}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={handleButtonClick}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        disabled={!actions.length}
      >
        <FiPlus aria-hidden="true" />
        {showLabel && <span>New</span>}
      </button>

      {open && typeof document !== "undefined" &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="menu"
            className="quick-actions-menu border shadow-sm"
            aria-labelledby={buttonId}
            style={{
              top: position.top,
              left: position.left,
              "--qa-arrow-left": `${position.arrowLeft}px`,
            }}
            onKeyDown={handleMenuKeyDown}
          >
            {actions.map((action, index) => (
              <button
                key={action.key}
                role="menuitem"
                type="button"
                className="quick-actions-menu__item"
                onClick={() => selectAction(action)}
                ref={(el) => {
                  itemRefs.current[index] = el;
                }}
                tabIndex={-1}
              >
                <span className="quick-actions-menu__icon">
                  <action.Icon aria-hidden="true" />
                </span>
                <span className="quick-actions-menu__copy">
                  <span className="quick-actions-menu__label">{action.label}</span>
                  <small>{action.description}</small>
                </span>
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
}
